import { useMemo } from 'react';

const CROCKFORD_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

// Custom epoch: 2025-01-01T00:00:00Z
// Extends the 40-bit range to ~2059 instead of ~2004
const CUSTOM_EPOCH = 1735689600000n;

const TIMESTAMP_CHARS = 8;  // 40 bits
const RANDOM_CHARS = 12;    // 60 bits

function encodeCrockford(value: bigint, length: number): string {
  const chars: string[] = new Array(length);
  for (let i = length - 1; i >= 0; i--) {
    chars[i] = CROCKFORD_ALPHABET[Number(value & 0x1Fn)];
    value >>= 5n;
  }
  return chars.join('');
}

function generateCryptoRandom(bits: number): bigint {
  const bytes = new Uint8Array(Math.ceil(bits / 8));
  crypto.getRandomValues(bytes);
  let value = 0n;
  for (const byte of bytes) {
    value = (value << 8n) | BigInt(byte);
  }
  // Mask to exact bit count
  return value & ((1n << BigInt(bits)) - 1n);
}

/**
 * Generate a unique contract number using a timestamp + crypto-random approach.
 *
 * Format: XXXX-XXXX-XXXX-XXXX-XXXX
 *   - First 8 chars (40 bits): Timestamp relative to 2025-01-01 epoch
 *   - Last 12 chars (60 bits): Crypto-random for uniqueness
 *
 * The result is sortable by creation time and collision-resistant.
 */
export function generateContractNumber(): string {
  const now = BigInt(Date.now()) - CUSTOM_EPOCH;

  if (now < 0n) {
    throw new Error('System clock is before custom epoch');
  }
  if (now >= (1n << 40n)) {
    throw new Error('Timestamp overflow: epoch range exhausted');
  }

  const timestamp = encodeCrockford(now, TIMESTAMP_CHARS);
  const random = encodeCrockford(generateCryptoRandom(60), RANDOM_CHARS);

  const raw = timestamp + random;

  // Format: XXXX-XXXX-XXXX-XXXX-XXXX
  return `${raw.slice(0, 4)}-${raw.slice(4, 8)}-${raw.slice(8, 12)}-${raw.slice(12, 16)}-${raw.slice(16, 20)}`;
}

/**
 * Hook that generates a stable contract number for create/clone forms.
 * The number is generated once per component mount and remains stable
 * across re-renders (idempotent).
 */
export function useContractNumber(): string {
  return useMemo(() => generateContractNumber(), []);
}
